"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const referralInfo = [{
  title: "Patient Information",
  description: "Patient name, date of birth, contact information, insurance information, and reason for referral."
}, {
  title: "Relevant Records",
  description: "Recent office notes, medication list, imaging reports, pulmonary testing, sleep study records, or hospital discharge paperwork when available."
}, {
  title: "Reason for Referral",
  description: "Briefly note the primary concern, such as COPD, asthma, chronic cough, shortness of breath, sleep apnea, oxygen needs, abnormal imaging, or post-hospital pulmonary follow-up."
}, {
  title: "Contact Information",
  description: "Include the referring office name, provider name, phone number, fax number, and preferred method for follow-up communication."
}];
export default function CommonReasonsForReferral() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="530" id="common-reasons-for-referral" className="bg-background text-foreground relative overflow-hidden py-20 sm:py-28 lg:py-36" style={{
    backgroundImage: "radial-gradient(ellipse at center, var(--background) 0%, var(--accent) 100%)"
  }}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-12">
            <span className="font-mono text-xs sm:text-sm tracking-widest uppercase text-primary">
              Referral Checklist
            </span>
          </div>
          <div className="lg:col-span-10 lg:col-start-2">
            <h2 className="font-serif font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[1.05] tracking-tight text-foreground">
              Helpful Information to Include
            </h2>
          </div>
        </div>

        <div ref={gridRef} className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {referralInfo.map((info, i) => <Card key={i} data-index={i} className={`border-border/40 bg-background transition-all duration-700 ease-out rounded-lg shadow-md ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: gridVisible ? `${i * 100}ms` : "0ms"
        }}>
              <CardHeader className="pb-2">
                <CardTitle className="font-serif text-xl sm:text-2xl text-foreground">
                  {info.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[15px] leading-relaxed text-muted-foreground font-default">
                  {info.description}
                </p>
              </CardContent>
            </Card>)}
        </div>

        <div className="mt-16 flex justify-center">
          <Button variant="ghost" className="font-mono text-xs sm:text-sm tracking-widest uppercase text-foreground hover:bg-transparent hover:text-primary group/btn px-0">
            <span className="inline-flex items-center gap-3">
              <span className="h-px w-8 bg-border" style={{
              backgroundColor: "var(--primary)"
            }} />
              <span>Fax Referral: (412) 533-5622</span>
            </span>
          </Button>
        </div>
      </div>
    </section>;
}